import type { AnalyticsQueryAction } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import type {
  InternalQuery,
  UrlSyncedQuery,
} from "@v2/features/analytics/utils/query";
import type { FilterCondition } from "schemas/generated/analyticsRead";
import type {
  ChartInterval,
  ComparisonTimeFrame,
  RelativeTimeFrame,
  SelectedTimePeriod,
} from "schemas/utils/analytics";
import type { AnalyticsUrlParamsFilterTypes } from "./moreFilters/constants";

import * as React from "react";

import { isCustomDateRange } from "@v1/features/analytics/analytics";
import { getChartIntervalOptions } from "@v1/features/analytics/chart";
import { AnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import {
  addEntryToUrlParams,
  removeEntryFromUrlParams,
} from "@v1/features/analytics/moreFilters/utils";
import useCurrentAnalyticsTab from "@v1/features/analytics/useCurrentAnalyticsTab";
import useCurrentAnalyticsTimezone from "@v1/features/analytics/useCurrentAnalyticsTimezone";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import {
  DEFAULT_FILTERS,
  DEFAULT_QUERY,
} from "@v2/features/analytics/constants";
import useUrlHosts from "@v2/features/analytics/hooks/useUrlHosts";
import { generatePathWithCompressedUrlSyncedQuery } from "@v2/features/analytics/utils/deepDiveNavigation";
import { generateDefaultUrlSyncedQuery } from "@v2/features/analytics/utils/query";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { routes } from "@v2/utils/router";
import { COMPARE_PREVIOUS_PERIOD } from "analytics-utils/analytics";
import isEqual from "lodash-es/isEqual";
import { generatePath, useNavigate, useSearchParams } from "react-router-dom";
import {
  compressObjectToLzString,
  decompressObjectFromLzString,
} from "replo-utils/lib/lzString";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

/**
 * The AnalyticsContext is the ONLY place where we should be manipulating fields
 * that will be used in the AnalyticsQuery.
 *
 * It exposes the query, which is a combination of the urlSyncedQuery (fields that are synced with the URL)
 * and internalQuery (other fields that don't need to be synced with the URL). To update the query (whether
 * it's the urlSyncedQuery or internalQuery), we should use the dispatchAnalyticsQuery() function.
 *
 * We should NEVER be directly accessing the URL to update fields in there, otherwise
 * we'll result in a state where our URL fields are not in sync with the context -> 2 sources of truth issue.
 *
 * @author Max 2024-12-15
 */
const AnalyticsContext: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();

  const urlHostsObj = useUrlHosts();
  const timeZone = useCurrentAnalyticsTimezone();

  const defaultUrlSyncedQuery = generateDefaultUrlSyncedQuery({
    urlHosts: urlHostsObj.data ?? [],
  });

  const analytics = useLogAnalytics();
  const navigate = useNavigate();

  const analyticsTab = useCurrentAnalyticsTab();

  const compressedUrlSyncedQuery = searchParams.get("query");

  // Returns the urlSyncedQuery that was stored in the URL, under the ?query='' param.
  // If there wasn't a ?query='' param in the URL, it returns the defaultUrlSyncedQuery.
  const urlSyncedQuery = compressedUrlSyncedQuery
    ? decompressObjectFromLzString<UrlSyncedQuery>(compressedUrlSyncedQuery)
    : defaultUrlSyncedQuery;

  const [internalQuery, setInternalQuery] =
    React.useState<InternalQuery>(DEFAULT_QUERY);

  // ALWAYS use this function to update the urlSyncedQuery. This will ensure that it's
  // synced with the URL, under the ?query='' param. This function updates both the context
  // and the URL params.
  const updateUrlSyncedQuery = (urlSyncedQuery: UrlSyncedQuery) => {
    const compressedUrlSyncedQuery = compressObjectToLzString(urlSyncedQuery);

    setSearchParams(
      (prev) => {
        prev.set("query", compressedUrlSyncedQuery);
        return prev;
      },
      { replace: true },
    );
  };

  /**
   * NOTE (Max, 2024-12-16):
   * On initial load, defaultUrlSyncedQuery won't have any urlHosts (as it's using the
   * useUrlHosts() hook, and it won't have had time to fetch the urlHosts
   * on initial load, so the urlHosts will be empty).
   *
   * This means that on initial load, IF there was no ?query='', then urlSyncedQuery
   * will have no urlHosts (as if there's no ?query='' it'll use defaultUrlSyncedQuery,
   * which would have no urlHosts on initial load, as explained above).
   *
   * So as soon as the useUrlHosts() hook has fetched the urlHosts, the
   * defaultUrlSyncedQuery will have urlHosts, and we'll need to update the urlSyncedQuery
   * to contain these urlHosts (again, only if there was no ?query='', as this would mean
   * urlHosts were empty).
   *
   * This below is essentially doing: "if urlSyncedQuery doesn't have any urlHosts, AND
   * defaultUrlSyncedQuery now has urlHosts (as they loaded), then update the urlSyncedQuery
   * (which will also update the ?query='')"
   */
  if (
    urlSyncedQuery.urlHosts?.length === 0 &&
    !isEqual(urlSyncedQuery.urlHosts, defaultUrlSyncedQuery.urlHosts)
  ) {
    updateUrlSyncedQuery({
      ...urlSyncedQuery,
      urlHosts: defaultUrlSyncedQuery.urlHosts,
    });
  }

  const resetInternalAndUrlSyncedQueries = () => {
    setInternalQuery(DEFAULT_QUERY);
    updateUrlSyncedQuery(defaultUrlSyncedQuery);
  };

  const dispatchAnalyticsQuery = (action: AnalyticsQueryAction) => {
    exhaustiveSwitch(
      action,
      "type",
    )({
      updatePagination: (action) => {
        setInternalQuery({
          ...internalQuery,
          offset: action.payload,
        });
      },
      sortMetric: (action) => {
        const { sortKey, sortOrder } = action.payload;

        setInternalQuery({
          ...internalQuery,
          sortMetric: sortKey,
          order: sortOrder,
          offset: 0,
        });
      },
      urlHosts: (action) => {
        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          urlHosts: action.payload,
        });
      },
      updateMainRange: (action) => {
        const selectedTimeFrameValue = action.payload;

        const selectedTimePeriod: SelectedTimePeriod = isCustomDateRange(
          selectedTimeFrameValue,
        )
          ? {
              type: "custom",
              value: {
                from: selectedTimeFrameValue.from.getTime(),
                to: selectedTimeFrameValue.to.getTime(),
              },
            }
          : {
              type: "relative",
              value: selectedTimeFrameValue as RelativeTimeFrame,
            };

        const { intervalOptions } = getChartIntervalOptions(
          selectedTimePeriod,
          timeZone,
        );

        const defaultChartInterval = intervalOptions[0]?.value ?? "day";

        const chartInterval = intervalOptions
          .map((option) => option.value)
          .includes(urlSyncedQuery.chartInterval)
          ? urlSyncedQuery.chartInterval
          : (defaultChartInterval as ChartInterval);

        setInternalQuery({
          ...internalQuery,
          offset: 0,
        });

        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          selectedTimePeriod,
          chartInterval,
        });

        analytics("analytics.time.filter", {
          range:
            typeof selectedTimeFrameValue === "string"
              ? selectedTimeFrameValue
              : "custom",
          location: analyticsTab,
        });
      },
      updateCompareRange: (action) => {
        const selectedComparePeriodValue = action.payload;

        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          selectedComparePeriod: isCustomDateRange(selectedComparePeriodValue)
            ? {
                type: "custom",
                value: {
                  from: selectedComparePeriodValue.from.getTime(),
                  to: selectedComparePeriodValue.to.getTime(),
                },
              }
            : {
                type: "relative",
                value: selectedComparePeriodValue as ComparisonTimeFrame,
              },
        });

        analytics("analytics.compare", {
          compareDetail:
            selectedComparePeriodValue === COMPARE_PREVIOUS_PERIOD
              ? "previous-period"
              : "custom",
          location: analyticsTab,
        });
      },
      /**
       * NOTE (Max, 2024-12-15): For the urlPath <Input />, we're using the CONTAINS operator
       * to find all urlPaths that contain the search term. When the search term updates, we
       * simply update urlSyncedQuery so that the CONTAINS operator contains the new searchTerm.
       */
      "filters.updateUrlPathSearch": (action) => {
        const searchTerm = action.payload;
        const urlPathFiltersWithoutContains =
          urlSyncedQuery.filters.urlPath.filter(
            (filterCondition) => filterCondition.operator !== "contains",
          );

        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          filters: {
            ...urlSyncedQuery.filters,
            urlPath: [
              ...urlPathFiltersWithoutContains,
              {
                operator: "contains",
                value: searchTerm,
              },
            ],
          },
        });
      },
      "filters.replaceUrlPath": (action) => {
        const urlPath = action.payload;

        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          filters: {
            ...urlSyncedQuery.filters,
            urlPath: [
              {
                operator: "equals",
                value: [urlPath],
              },
            ],
          },
        });
      },
      updateChartInterval: (action) => {
        const chartInterval = action.payload;

        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          chartInterval,
        });
      },

      initDeepDivePage: (action) => {
        const { urlPath } = action.payload;

        const newUrlPathFilters: FilterCondition[] = [
          {
            operator: "equals",
            value: [urlPath],
          },
        ];

        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          filters: {
            ...urlSyncedQuery.filters,
            urlPath: newUrlPathFilters,
          },
        });
      },
      initOverviewPage: () => {
        /**
         * NOTE (Max, 2024-12-14): If we were on the deep dive page, then the urlSyncQuery will have
         * the urlPathFilters set to be equal to whatever urlPath we were on the deep dive page. If we don't
         * do the following operation on initial mount, then we'll just be querying for that 1 urlPath.
         */
        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          filters: {
            ...urlSyncedQuery.filters,
            urlPath: DEFAULT_FILTERS.urlPath,
          },
        });
      },
      "filters.pageType": (action) => {
        const pageType = action.payload;

        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          filters: { ...urlSyncedQuery.filters, pageType },
        });

        analytics("analytics.report.switch", {
          switchTo: pageType === "entryPages" ? "entry_page" : "sessions",
          location: analyticsTab,
        });
      },
      "filters.deleteUrlParam": (action) => {
        const { attribute, operator, value } = action.payload;

        const urlParamToRemove = {
          attribute,
          operator,
          value,
        };

        const updatedUrlParams = removeEntryFromUrlParams({
          currentUrlParams: urlSyncedQuery.filters.urlParams,
          urlParamToRemove,
        });

        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          filters: { ...urlSyncedQuery.filters, urlParams: updatedUrlParams },
        });
      },
      "filters.addUrlParam": (action) => {
        const updatedUrlParams = addEntryToUrlParams({
          currentUrlParams: urlSyncedQuery.filters.urlParams,
          urlParamToAdd: action.payload,
        });

        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          filters: { ...urlSyncedQuery.filters, urlParams: updatedUrlParams },
        });

        const { attribute, operator } = action.payload;

        analytics("analytics.filter", {
          type: attribute as AnalyticsUrlParamsFilterTypes,
          filterDetail: operator,
          location: analyticsTab,
        });
      },
      openDeepDivePageFromOverviewPage: (action) => {
        const { urlPath, source } = action.payload;

        analytics("analytics.record.click", {
          from: source,
        });

        const urlSyncedQueryWithUrlPathFilter: UrlSyncedQuery = {
          ...urlSyncedQuery,
          filters: {
            ...urlSyncedQuery.filters,
            urlPath: [
              {
                operator: "equals",
                value: [urlPath],
              },
            ],
          },
        };

        const deepDivePath = Boolean(projectId)
          ? generatePath(routes.shops.analytics.pageDetails, {
              projectId,
            })
          : generatePath(routes.analytics.pageDetails, {
              workspaceId,
            });

        const deepDivePathWithCompressedUrlSyncedQuery =
          generatePathWithCompressedUrlSyncedQuery({
            path: deepDivePath,
            urlSyncedQueryConfig: {
              type: "decompressed",
              urlSyncedQuery: urlSyncedQueryWithUrlPathFilter,
            },
          });

        navigate(deepDivePathWithCompressedUrlSyncedQuery);
      },
      toggleFractional: (action) => {
        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          isFractional: action.payload,
        });
        analytics("analytics.report.switch", {
          switchTo: "fractional",
          location: analyticsTab,
        });
      },
      updatePageTypeAndFractional: (action) => {
        const { pageType, isFractional } = action.payload;

        updateUrlSyncedQuery({
          ...urlSyncedQuery,
          filters: { ...urlSyncedQuery.filters, pageType },
          isFractional,
        });

        analytics("analytics.report.switch", {
          switchTo: pageType === "entryPages" ? "entry_page" : "sessions",
          location: analyticsTab,
        });
      },
    });
  };

  const contextValues = {
    resetInternalAndUrlSyncedQueries,
    dispatchAnalyticsQuery,
    query: { ...internalQuery, ...urlSyncedQuery },
    urlHostsObj,
    workspaceId: workspaceId ?? undefined,
    projectId,
  };

  return (
    <AnalyticsQueryContext.Provider value={contextValues}>
      {children}
    </AnalyticsQueryContext.Provider>
  );
};

export default AnalyticsContext;
